import { useEffect, useState } from 'react';
import { Paper, TextInput, PasswordInput, Button, Title, Center, Stack, Alert, Group, Divider, Text } from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import { useAuth } from '../store/auth';
import { api } from '../api/client';

// Maps the short ?sso_error codes set by GET /auth/oidc/callback to friendly,
// non-leaky messages.
const SSO_ERRORS: Record<string, string> = {
  disabled: 'Microsoft sign-in is not configured on this server.',
  provider: 'Microsoft sign-in was cancelled or failed. Please try again.',
  state: 'Your sign-in session expired. Please try again.',
  token: 'Could not verify your Microsoft sign-in. Please try again.',
  not_member: 'Only Lumen employee accounts can sign in.',
  domain: 'Only @dotlumen.com accounts can sign in here.',
  not_authorized: "Your Lumen account isn't authorized for CYCLOTRON yet — ask an admin to add you.",
};

function MicrosoftMark() {
  return (
    <svg width="16" height="16" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // On mount: surface any SSO error passed back via the URL, then clean it up;
  // and ask the backend whether the Microsoft button should be shown.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('sso_error');
    if (code === 'request_pending') {
      // Not an error — a real Lumen user signed in but has no account yet; a
      // request was created for admin approval.
      setNotice("Access request submitted. You signed in with Microsoft but don’t have a CYCLOTRON account yet — an admin will review your request, and you’ll be able to sign in once it’s approved.");
      window.history.replaceState({}, '', window.location.pathname);
    } else if (code) {
      setErr(SSO_ERRORS[code] ?? 'Microsoft sign-in failed. Please try again.');
      window.history.replaceState({}, '', window.location.pathname);
    }
    api.get<{ enabled: boolean }>('/auth/oidc/status')
      .then((s) => setSsoEnabled(!!s.enabled))
      .catch(() => setSsoEnabled(false));
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await login(username, password);
      navigate('/orders');
    } catch (e: any) {
      setErr(e.message || 'Login failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Center h="100vh">
      <Paper p="xl" shadow="sm" withBorder w={360}>
        <Stack>
          <Group justify="center" gap={8}>
            <Logo size={40} />
            <Title order={2} style={{ letterSpacing: '0.1em' }}>CYCLOTRON</Title>
          </Group>

          {notice && <Alert color="blue" variant="light">{notice}</Alert>}
          {err && <Alert color="red" variant="light">{err}</Alert>}

          {ssoEnabled && (
            <>
              <Button
                variant="default"
                fullWidth
                leftSection={<MicrosoftMark />}
                onClick={() => { window.location.assign('/api/v1/auth/oidc/login'); }}
              >
                Sign in with Microsoft
              </Button>
              <Divider label="or sign in with a password" labelPosition="center" />
            </>
          )}

          <form onSubmit={onSubmit}>
            <Stack>
              <TextInput
                label="Username"
                value={username}
                onChange={(e) => setUsername(e.currentTarget.value)}
                autoFocus={!ssoEnabled}
                required
              />
              <PasswordInput
                label="Password"
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                required
              />
              <Button type="submit" loading={busy} fullWidth>Log in</Button>
            </Stack>
          </form>

          {ssoEnabled && (
            <Text size="xs" c="dimmed" ta="center">
              Password sign-in is for break-glass / admin access. Lumen staff should use Microsoft.
            </Text>
          )}
        </Stack>
      </Paper>
    </Center>
  );
}
